<template>
  <div
    class="city-popover"
    :class="{
      'city-popover--start': alignment === 'start',
      'city-popover--mobile-burger': mobileAnchor === 'burger',
    }"
    role="dialog"
    :aria-labelledby="titleId"
    @click.stop
  >
    <div class="city-popover__arrow" aria-hidden="true"></div>
    <div class="city-popover__body">
      <div class="city-popover__content">
        <p :id="titleId" class="city-popover__title">
          Вы в {{ cityName }}?
        </p>
        <p class="city-popover__subtitle">
          От города зависит доступность и актуальность информации
        </p>
      </div>
      <button
        type="button"
        class="city-popover__confirm"
        @click="emit('confirm')"
      >
        Да, верно
      </button>
      <button
        type="button"
        class="city-popover__change"
        @click="emit('change')"
      >
        Выбрать другой город
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUniqueId } from '~/composables/useUniqueId'

defineProps<{
  cityName: string
  alignment?: 'end' | 'start'
  mobileAnchor?: 'burger'
}>()

const emit = defineEmits<{
  confirm: []
  change: []
}>()

const titleId = useUniqueId('city-popover-title')
</script>

<style lang="scss">
.city-popover {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  right: auto;
  transform: translateX(-50%);
  z-index: 20;
  width: 272px;
  max-width: min(272px, calc(100vw - 24px));

  &__arrow {
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid $color-white;
    filter: drop-shadow(0 -1px 2px rgba(138, 138, 176, 0.08));
  }

  &__body {
    box-sizing: border-box;
    background: $color-white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(138, 138, 176, 0.16);
    padding: 12px 20px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    text-align: center;
    width: 100%;
    min-width: 0;

    /* common.scss задаёт p + p { margin-top: 24px } — ломает макет (в Figma 8px между title и subtitle) */
    p + p {
      margin-top: 0;
    }
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.35;
    color: $color-black;
  }

  &__subtitle {
    margin: 0;
    font-size: 14px;
    line-height: 1.35;
    color: $color-gray;
    overflow-wrap: break-word;
  }

  &__confirm {
    appearance: none;
    border: none;
    margin: 0;
    cursor: pointer;
    font: inherit;
    width: 100%;
    box-sizing: border-box;
    padding: 12px 16px;
    border-radius: 50px;
    background-color: $color-purple;
    color: $color-on-accent;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    text-align: center;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 0.9;
    }
  }

  &__change {
    appearance: none;
    background: none;
    border: none;
    margin: 0;
    padding: 0;
    cursor: pointer;
    font: inherit;
    font-size: 12px;
    line-height: 1.35;
    color: $color-black;
    text-align: center;
    width: 100%;
    transition: color 0.15s ease;

    &:hover {
      color: $color-accent-text;
    }
  }

  @include media(header-nav) {
    width: calc(100vw - 32px);
    max-width: 272px;
  }

  &--mobile-burger {
    position: fixed;
    top: var(--city-popover-top, 0);
    left: var(--city-popover-left, 50%);
    right: auto;
    transform: translateX(-50%) translateX(var(--city-popover-shift-x, 0px));
    z-index: 120;
    width: min(272px, calc(100vw - 32px));
    max-width: min(272px, calc(100vw - 32px));

    .city-popover__arrow {
      left: calc(50% - var(--city-popover-shift-x, 0px));
      right: auto;
      transform: translateX(-50%);
    }
  }

  &--start {
    left: 0;
    right: auto;
    transform: none;
    max-width: min(272px, calc(100vw - 32px));

    .city-popover__arrow {
      left: 28px;
      transform: none;
    }

    @include media(header-nav) {
      right: auto;
      left: 0;
    }
  }
}
</style>
